import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useLocation } from "react-router-dom";
import useSignIn from 'react-auth-kit/hooks/useSignIn';
import { Button, Input, Form, Typography, Alert, Card } from "antd";
import useLogin from "../../api/auth/useLogin";
import LayoutPlain from "../../components/LayoutPlain";
import { LoginParam } from "types/login";

const { Title, Text } = Typography;

const Login = () => {
    const { control, handleSubmit, formState: { errors } } = useForm<LoginParam>();
    const login = useLogin();
    const signIn = useSignIn();
    const location = useLocation();
    const from = new URLSearchParams(location.search).get("from") || "/";

    const [showErrorLogin, setShowErrorLogin] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const onSubmit = async (data: LoginParam) => {
        if (login.isLoading) return;

        login.mutateAsync(data)
            .then(res => {
                if (res === undefined) {
                    setShowErrorLogin(true);
                    return;
                }

                if (res.data?.success) {
                    if (signIn({
                        auth: { token: res.data.data.token, type: 'Bearer' },
                        userState: { token: res.data.data.token },
                    })) {
                        window.location.replace(from);
                        console.log("Sign in successful, redirecting...");
                    } else {
                        throw new Error("Terjadi kesalahan saat login, silahkan coba lagi.");
                    }
                } else {
                    throw new Error(res.data.message || "Terjadi kesalahan saat login, silahkan coba lagi.");
                }
            })
            .catch(error => {
                setShowErrorLogin(true);
                setErrorMessage(error?.message || "Login gagal, silakan coba lagi.");
            });
    };

    useEffect(() => {
        setShowErrorLogin(false);
    }, [location]);

    return (
        <LayoutPlain>
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <Card className="border border-gray-200 shadow-sm">
                        <div className="p-6">
                            {/* Header Section */}
                            <div className="text-center mb-8">
                                <div className="mb-4">
                                    <img
                                        src="/sayursembalun-logo.png"
                                        alt="Sayur Sembalun Logo"
                                        className="mx-auto h-16 w-auto"
                                    />
                                </div>
                                <Title level={3} className="!mb-2 text-gray-900">
                                    Sayur Sembalun Dashboard
                                </Title>
                                <Text className="text-gray-600">
                                    Masuk ke akun Anda untuk melanjutkan
                                </Text>
                            </div>

                            {/* Error Alert */}
                            {showErrorLogin && (
                                <div className="mb-6">
                                    <Alert
                                        type="error"
                                        message={errorMessage}
                                        showIcon
                                        className="border-red-200"
                                    />
                                </div>
                            )}

                            {/* Login Form */}
                            <Form
                                layout="vertical"
                                onFinish={handleSubmit(onSubmit)}
                                className="space-y-4"
                            >
                                <Form.Item
                                    label={<span className="text-gray-700 font-medium">Email</span>}
                                    validateStatus={errors.email ? "error" : ""}
                                    help={errors.email?.message}
                                    className="mb-4"
                                >
                                    <Controller
                                        name="email"
                                        control={control}
                                        rules={{
                                            required: "Email tidak boleh kosong",
                                            pattern: {
                                                value: /^\S+@\S+$/i,
                                                message: "Format email tidak valid"
                                            }
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Masukkan email Anda"
                                                size="large"
                                                className="border-gray-300"
                                            />
                                        )}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label={<span className="text-gray-700 font-medium">Password</span>}
                                    validateStatus={errors.password ? "error" : ""}
                                    help={errors.password?.message}
                                    className="mb-6"
                                >
                                    <Controller
                                        name="password"
                                        control={control}
                                        rules={{ required: "Password tidak boleh kosong" }}
                                        render={({ field }) => (
                                            <Input.Password
                                                {...field}
                                                placeholder="Masukkan password Anda"
                                                size="large"
                                                className="border-gray-300"
                                            />
                                        )}
                                    />
                                </Form.Item>

                                <Form.Item className="mb-0">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        size="large"
                                        loading={login.isLoading}
                                        className="h-12 font-medium"
                                    >
                                        {login.isLoading ? "Sedang masuk..." : "Masuk"}
                                    </Button>
                                </Form.Item>
                            </Form>

                            {/* Footer Section - Uncomment if needed */}
                            {/* 
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="text-center">
                                    <Link 
                                        to="/forgot-password"
                                        className="text-sm text-blue-600 hover:text-blue-500"
                                    >
                                        Lupa password?
                                    </Link>
                                </div>
                                
                                <div className="text-center mt-4">
                                    <Text className="text-sm text-gray-600">
                                        Belum punya akun?{" "}
                                        <Link 
                                            to="/register"
                                            className="text-blue-600 hover:text-blue-500 font-medium"
                                        >
                                            Daftar sekarang
                                        </Link>
                                    </Text>
                                </div>
                            </div>
                            */}
                        </div>
                    </Card>

                    {/* Additional Info */}
                    {/* <div className="text-center mt-6">
                        <Text className="text-xs text-gray-500">
                            © 2024 Sayur Sembalun. All rights reserved.
                        </Text>
                    </div> */}
                </div>
            </div>
        </LayoutPlain>
    );
};

export default Login;